import { TextInput, PageLoaderFull } from "@jugl-web/ui-components";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslations, useToast } from "@jugl-web/utils";
import { CountryPicker } from "@jugl-web/ui-components/web";
import { CountryPickerValue } from "@jugl-web/ui-components/web/CountryPicker/types";
import { isValidEmail } from "@jugl-web/utils/validators";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { CX_BUSINESS_NAME_KEY, getStorageItem } from "@jugl-web/utils/storage";
import {
  MAX_NAME_LENGTH,
  MAX_WORKSPACE_NAME_LENGTH,
} from "@web-src/modules/auth/conts";
import { Button } from "@jugl-web/ui-kit";
import { useDefaultCountry } from "../../hooks/useDefaultCountry";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";

interface CXWorkspaceFormProps {
  onSuccess: (entityId: string) => void;
  showProfileFields?: boolean;
  initialEmail?: string;
}

export const CXWorkspaceForm: React.FC<CXWorkspaceFormProps> = ({
  onSuccess,
  showProfileFields,
  initialEmail,
}) => {
  const { t } = useTranslations();
  const { toast } = useToast({ variant: "web" });
  const { entitiesApi, usersApi } = useRestApiProvider();

  const [createProfile] = usersApi.useCreateProfileMutation();
  const [createEntity] = entitiesApi.useCreateEntityMutation();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [workspaceName, setWorkspaceName] = useState("");
  const [workspaceCountry, setWorkspaceCountry] =
    useState<CountryPickerValue>();
  const [workspaceEmail, setWorkspaceEmail] = useState("");
  const [isWorkspaceSubmitted, setIsWorkspaceSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Pre-fill workspace email
  useEffect(() => {
    if (initialEmail) {
      setWorkspaceEmail(initialEmail);
    }
  }, [initialEmail]);

  const defaultCountry = useDefaultCountry();

  // Pre-fill country from API response
  useEffect(() => {
    if (defaultCountry && !workspaceCountry) {
      setWorkspaceCountry(defaultCountry);
    }
  }, [defaultCountry, workspaceCountry]);

  // Auto-update workspace name
  useEffect(() => {
    const storedBusinessName = getStorageItem<string>(CX_BUSINESS_NAME_KEY);
    if (storedBusinessName) {
      setWorkspaceName(storedBusinessName);
    } else if (firstName) {
      setWorkspaceName(`${firstName}'s Workspace`);
    }
  }, [firstName]);

  const workspaceEmailError = useMemo(() => {
    if (!isWorkspaceSubmitted) return undefined;
    if (!workspaceEmail.length) {
      return t({
        id: "form-controls.email.error-is-required",
        defaultMessage: "Email is empty",
      });
    }
    if (!isValidEmail(workspaceEmail)) {
      return t({
        id: "form-controls.email.error-is-incorrect",
        defaultMessage: "Email is incorrect",
      });
    }
    return undefined;
  }, [isWorkspaceSubmitted, workspaceEmail, t]);

  const allWorkspaceFieldsHaveValues =
    (!showProfileFields || (!!firstName.trim() && !!lastName.trim())) &&
    !!workspaceName.trim() &&
    !!workspaceCountry?.iso &&
    !!workspaceEmail;

  const handleWorkspaceSubmit = useCallback(async () => {
    setIsWorkspaceSubmitted(true);
    if (
      (showProfileFields && (!firstName.trim() || !lastName.trim())) ||
      !workspaceName.trim() ||
      !workspaceCountry?.iso ||
      !isValidEmail(workspaceEmail)
    ) {
      return;
    }

    setIsLoading(true);
    try {
      const [, entityResult] = await Promise.all([
        showProfileFields
          ? createProfile({
              first_name: firstName.trim(),
              last_name: lastName.trim(),
            }).unwrap()
          : Promise.resolve(null),
        createEntity({
          type: "cx",
          data: {
            name: workspaceName.trim(),
            country: workspaceCountry.iso,
            billing_email: workspaceEmail ?? undefined,
          },
        }).unwrap(),
      ]);

      onSuccess(entityResult.id);
    } catch {
      toast(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong.",
        }),
        { variant: "error" }
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    createEntity,
    createProfile,
    firstName,
    lastName,
    onSuccess,
    showProfileFields,
    t,
    toast,
    workspaceCountry?.iso,
    workspaceEmail,
    workspaceName,
  ]);

  return (
    <>
      <PageLoaderFull isActive={isLoading} isTransparent />
      <div className="my-auto flex w-[500px] flex-col items-center justify-center py-4">
        <span className="text-dark font-secondary text-[32px] font-medium leading-[48px]">
          {t({
            id: "cx-ai-agents-page.lets-start-with-essentials",
            defaultMessage: "Let's start with essentials",
          })}
        </span>
        <span className="text-grey-900 font-secondary mt-2 text-sm leading-4">
          {t({
            id: "cx-ai-agents-page.just-a-few-details",
            defaultMessage: "Just a few details to start with",
          })}
        </span>
        <div className="mt-10 flex w-full flex-col gap-6">
          {showProfileFields && (
            <div className="flex gap-4">
              <TextInput
                placeholder={t({
                  id: "common.enter",
                  defaultMessage: "Enter",
                })}
                label={t({
                  id: "common.first-name",
                  defaultMessage: "First Name",
                })}
                isRequired
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                isInvalid={isWorkspaceSubmitted && !firstName.trim()}
                classNames={{ wrapperClassName: "flex-1" }}
                maxLength={MAX_NAME_LENGTH}
              />
              <TextInput
                placeholder={t({
                  id: "common.enter",
                  defaultMessage: "Enter",
                })}
                label={t({
                  id: "common.last-name",
                  defaultMessage: "Last Name",
                })}
                isRequired
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                isInvalid={isWorkspaceSubmitted && !lastName.trim()}
                classNames={{ wrapperClassName: "flex-1" }}
                maxLength={MAX_NAME_LENGTH}
              />
            </div>
          )}
          <div className="flex gap-4">
            <TextInput
              placeholder={t({
                id: "common.enter",
                defaultMessage: "Enter",
              })}
              label={t({
                id: "create-entity-form.workspace-name",
                defaultMessage: "Workspace Name",
              })}
              isRequired
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
              isInvalid={isWorkspaceSubmitted && !workspaceName.trim()}
              classNames={{ wrapperClassName: "flex-1" }}
              maxLength={MAX_WORKSPACE_NAME_LENGTH}
            />
            <CountryPicker
              countryIso={workspaceCountry?.iso}
              onSelect={(item) => setWorkspaceCountry(item)}
              placement="top"
              className="z-[9999]"
            >
              {() => (
                <TextInput
                  value={
                    workspaceCountry?.name
                      ? `${workspaceCountry.name} (${workspaceCountry.iso})`
                      : ""
                  }
                  isRequired
                  label={t({
                    id: "create-entity-form.workspace-country",
                    defaultMessage: "Workspace Country",
                  })}
                  placeholder={t({
                    id: "common.select",
                    defaultMessage: "Select",
                  })}
                  classNames={{
                    wrapperClassName: "flex-1",
                    inputClassName: "cursor-pointer",
                  }}
                  isInvalid={isWorkspaceSubmitted && !workspaceCountry?.iso}
                  readOnly
                />
              )}
            </CountryPicker>
          </div>
          <TextInput
            placeholder="email@gmail.com"
            label={t({
              id: "create-entity-form.workspace-email",
              defaultMessage: "Workspace Email",
            })}
            isRequired
            value={workspaceEmail}
            onChange={(e) => setWorkspaceEmail(e.target.value)}
            isInvalid={isWorkspaceSubmitted && !!workspaceEmailError}
            errorMessage={workspaceEmailError}
            type="email"
          />
        </div>
        <Button
          className="mt-10 w-[500px]"
          onClick={handleWorkspaceSubmit}
          isDisabled={!allWorkspaceFieldsHaveValues || isLoading}
          isLoading={isLoading}
          iconEnd={<ArrowIcon />}
        >
          {t({
            id: "cx-ai-agents-page.create-workspace",
            defaultMessage: "Create Workspace",
          })}
        </Button>
      </div>
    </>
  );
};
